<!-- eslint-disable @typescript-eslint/no-magic-numbers -->
<script setup lang="ts">
    import AllstarsRegister from '@/components/AllstarsRegister.ce.vue';
    import AmountCard from '@/components/cards/AmountCard.ce.vue';
    import AppointmentCalendarCard from '@/components/cards/AppointmentCalendarCard.ce.vue';
    import AppointmentCard from '@/components/cards/AppointmentCard.ce.vue';
    import BudgetCard from '@/components/cards/BudgetCard.ce.vue';
    import CalendarCard from '@/components/cards/CalendarCard.ce.vue';
    import FinalResultCard from '@/components/cards/FinalResultCard.ce.vue';
    import GrettingsCard from '@/components/cards/GreetingsCard.ce.vue';
    import LoadingCard from '@/components/cards/LoadingCard.ce.vue';
    import NameFieldCard from '@/components/cards/NameFieldCard.ce.vue';
    import OptionsCard from '@/components/cards/OptionsCard.ce.vue';
    import PathSelectionCard from '@/components/cards/PathSelectionCard.ce.vue';
    import PreTextCard from '@/components/cards/PreTextCard.ce.vue';
    import SurveyCard from '@/components/cards/SurveyCard.ce.vue';
    import PackageSelection from '@/components/packages/PackageSelection.ce.vue';
    import Quote from '@/components/quote/Quote.ce.vue';
    import { DESCRIPTIONS } from '@/constants/descriptions';
    import { VENUE_HOST_EVENT, VENUE_HOST_RESET } from '@/constants/events';
    import { SURVEY_APPOINTMENT, SURVEY_ORGANIZER, SURVEY_THEME } from '@/constants/survey';
    import { formatCurrencyNoCent } from '@/helpers/formatting';
    import { forceAncestorsToTop } from '@/helpers/scroll';
    import { type EventBusType } from '@/plugins/event-bus-plugin';
    import { type SurveyItem } from '@/typings/widget';
    import useEditableClone from '@shared/composables/useEditableClone';
    import { DateTime } from '@shared/helpers';
    import { type ComputedRef, type Ref } from 'vue';

    const props = defineProps<{
        venue: string
        maxGuestAmount?: number
        minGuestAmount?: number
        guestAmountStep?: number
        maxBudgetAmount?: number
        minBudgetAmount?: number
        budgetStep?: number
        locale: string
        error?: boolean
        brandColor?: string
    }>();

    const emit = defineEmits<{(event: 'backToMain' | 'openQuote' | 'callSelected' | 'scrollToTop')}>();

    const eventBus = inject<EventBusType>('eventBus');
    const { t } = useI18n();

    // Stores
    const pathStore = useWidgetPathStore();
    const {
        step,
        bookerName,
        eventTypeId,
        selectedPath,
        selectedDate,
        surveyOrganizer,
        surveyAppointment,
        surveyTheme,
        guestAmount,
        descriptionIds,
        budgetAmount,
        appointment,
        appointmentDate,
        pathAlreadySelected,
    } = storeToRefs(pathStore);
    const { resetPathStore } = pathStore;
    const { venueEventTypes, venue: venueData, isPackageActive, packageListFilter } = storeToRefs(useWidgetVenueStore());
    const { isQuoteOpen } = storeToRefs(useWidgetMenuStore());

    // Refs
    const scrollContainer: Ref<HTMLElement | null> = ref(null);
    const selectPackages: Ref<boolean> = ref(false);
    const selectQuote: Ref<boolean> = ref(false);
    const selectRegister: Ref<boolean> = ref(false);
    const surveyAppointmentItems: Ref<SurveyItem[]> = ref([]);

    // Computed
    // eslint-disable-next-line max-lines-per-function
    const finalSelectionMap: ComputedRef<{id: number, name: string, icon?: string}[]> = computed(() => {
        const eventTypeAnswer = {
            id: Math.floor(Math.random()),
            name: venueEventTypes.value.find(event => Number(event.id) === Number(eventTypeId.value))?.name ?? '',
        };
        const surveyOrganizerAnswer = {
            id: Math.floor(Math.random()),
            name: SURVEY_ORGANIZER.find(org => org.value === surveyOrganizer.value)?.label ?? '',
        };
        const surveyThemeAnswer = {
            id: Math.floor(Math.random()),
            name: SURVEY_THEME.find(thm => thm.value === surveyTheme.value)?.label ?? '',
        };
        const surveyAppointmentAnswer = {
            id: Math.floor(Math.random()),
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            name: SURVEY_APPOINTMENT.find(thm => thm.value === surveyAppointment.value)?.label ?? '',
        };
        const descriptionAnswers = descriptionIds.value.map(id => ({
            id: Math.floor(Math.random()),
            name: DESCRIPTIONS.find(desc => desc.id === id)?.name ?? '',
            icon: DESCRIPTIONS.find(desc => desc.id === id)?.icon ?? '',
        }));
        const guestAnswer = {
            id: Math.floor(Math.random()),
            name: `${guestAmount.value} ${t('widget.global.guests')}`,
        };
        const budgetAnswer = {
            id: Math.floor(Math.random()),
            name: formatCurrencyNoCent(
                budgetAmount.value, 'EUR', props.locale,
            ) ?? '',
        };

        return [
            eventTypeAnswer,
            guestAnswer,
            surveyOrganizerAnswer,
            surveyThemeAnswer,
            surveyAppointmentAnswer,
            ...descriptionAnswers,
            budgetAnswer,
        ] as {id: number, name: string, icon?: string}[];
    });
    const eventName: ComputedRef<string> = computed(() => venueEventTypes.value.find(event => Number(event.id) === Number(eventTypeId.value))?.name ?? '');

    // Methods
    const onVenueHost = (next: number): void => {
        eventBus?.emit(VENUE_HOST_EVENT, { step: next });
    };

    const handleToMain = (): void => {
        resetPathStore();
        emit('backToMain');
        eventBus?.emit(VENUE_HOST_RESET);
    };

    const scrollToTop = async(): Promise<void> => {
        // Wait for the next screen to render before resetting the scroll position.
        await nextTick();

        // Force the inner scroll container and every ancestor up the tree to the top.
        forceAncestorsToTop(scrollContainer.value);

        // Re-run after the browser has laid out the new screen (heights can change post-render,
        // e.g. PackageDetail's min-height calculation), so the reset is not left mid-page.
        requestAnimationFrame(() => { forceAncestorsToTop(scrollContainer.value) });

        // ...and bubble up so the host's main-content is reset too.
        emit('scrollToTop');
    };

    const onCalendarSelected = (next: number, difference: number): void => {
        eventBus?.emit(VENUE_HOST_EVENT, {
            step: next,
            param: { days: difference },
        });
    };

    const populateSurveyItems = async(value: string): Promise<void> => {
        surveyAppointmentItems.value = [];
        await nextTick(() => {
            const date = DateTime.dayjsObject(value);

            const [
                weekday, day, month, year,
            ] = date.format('dddd D MMMM YYYY')
                .split(' ');
            surveyAppointmentItems.value = useEditableClone(SURVEY_APPOINTMENT);
            surveyAppointmentItems.value[0].label = `${t('widget.survey-appointment.date-only')} ${weekday} ${day} ${month} ${year}`;
            surveyAppointmentItems.value[1].label = `${t('widget.survey-appointment.date-some-1')} ${weekday} ${t('widget.survey-appointment.date-some-2')} ${month} ${year}`;
        });
    };

    const onResetPath = async(): Promise<void> => {
        selectQuote.value = false;
        isQuoteOpen.value = false;

        await nextTick();

        handleToMain();
    };

    const handleSelectEventType = (nextStep: number): void => {
        const selectedEventType = venueEventTypes.value.find(event => Number(event.id) === eventTypeId.value);
        const isBusinessEvent = selectedEventType?.business;

        if (isBusinessEvent && isPackageActive.value) {
            selectPackages.value = true;
            step.value = 1;
            return;
        }

        if (isBusinessEvent && !isPackageActive.value) {
            emit('callSelected');
            return;
        }

        step.value = nextStep;
    };

    // Hooks
    onMounted(() => {
        step.value = 1;
    });

    // Watchers
    watch(() => step.value, () => {
        emit('scrollToTop');
    });

    watch(() => selectedDate.value, async nVal => {
        if (nVal) {
            await populateSurveyItems(nVal);
        }
    });

    watch(() => eventTypeId.value, nVal => {
        if (!nVal) return;
        packageListFilter.value.eventType = nVal;

        if (step.value !== 1) return;

        const selectedEventType = venueEventTypes.value.find(event => Number(event.id) === Number(nVal));
        if (!selectedEventType?.business) return;

        // Wait for OptionsCard's slide-up exit animation (0.75s CSS transition) to finish before mounting PackageSelection
        const EXIT_ANIMATION_DURATION = 750;
        setTimeout(() => {
            if (isPackageActive.value) {
                selectPackages.value = true;
                return;
            }
            emit('callSelected');
        }, EXIT_ANIMATION_DURATION);
    });
</script>

<template>
    <div class="path-view">
        <div
            v-if="selectPackages"
            ref="scrollContainer"
            class="path-view__scroll"
        >
            <PackageSelection
                :locale="locale"
                :venue="venue"
                :error="error"
                in-path
                @scroll-to-top="scrollToTop"
                @back-to-main="handleToMain"
                @back-to-path="selectPackages = false"
            />
        </div>
        <Quote
            v-else-if="selectQuote"
            :venue="venue"
            :locale="locale"
            :max-guest-amount="maxGuestAmount"
            :min-guest-amount="minGuestAmount"
            :guest-amount-step="guestAmountStep"
            in-path
            @back-to-path="onResetPath"
            @scroll-to-top="emit('scrollToTop')"
        />
        <AllstarsRegister
            v-else-if="selectRegister"
            :venue="venueData"
            :name="bookerName"
            @back-to-main="handleToMain"
            @back-to-path="selectRegister = false"
        />
        <template v-else>
            <OptionsCard
                v-if="[1, 1.5].includes(step)"
                v-model="eventTypeId"
                :items="venueEventTypes"
                :step="1"
                :title="t('widget.titles.event-type-title')"
                @next="handleSelectEventType"
            />
            <NameFieldCard
                v-if="[1.5, 2, 2.5].includes(step)"
                v-model="bookerName"
                :step="2"
                @next="step = $event"
                @selected="() => onVenueHost(2.5)"
            />
            <GrettingsCard
                v-if="[2.5, 3, 3.5].includes(step)"
                :step="3"
                :title="t('widget.titles.greetings-title', { name: bookerName })"
                :path-selected="pathAlreadySelected"
                animate
                @next="step = $event"
            />
            <PathSelectionCard
                v-if="[3.5, 4, 4.5].includes(step) && !pathAlreadySelected"
                v-model="selectedPath"
                :step="4"
                :brand-color="brandColor"
                :venue="venueData"
                @next="step = $event"
                @select-packages="selectPackages = true"
                @select-quote="selectQuote = true; isQuoteOpen = true;"
                @select-register="selectRegister = true;"
                @started="() => onVenueHost(3)"
                @selected="$event => onVenueHost($event - 1)"
            />
            <CalendarCard
                v-if="[4.5, 5, 5.5].includes(step)"
                v-model="selectedDate"
                :step="5"
                @next="step = $event"
                @selected="$event => onCalendarSelected(4, $event)"
                @exited="() => onVenueHost(4.5)"
            />
            <SurveyCard
                v-if="[5.5, 6, 6.5].includes(step)"
                v-model="surveyAppointment"
                :title="t('widget.titles.survey-appointment-title')"
                :subtitle="t('widget.titles.survey-appointment-subtitle')"
                :items="surveyAppointmentItems"
                :step="6"
                @next="step = $event"
                @exiting="() => onVenueHost(5)"
                @exited="() => onVenueHost(5.5)"
            />
            <SurveyCard
                v-if="[6.5, 7, 7.5].includes(step)"
                v-model="surveyOrganizer"
                :title="t('widget.titles.survey-organizer-title')"
                :subtitle="t('widget.titles.survey-organizer-subtitle')"
                :items="SURVEY_ORGANIZER"
                :step="7"
                @started="() => onVenueHost(6)"
                @exiting="() => onVenueHost(6.5)"
                @next="step = $event"
            />
            <SurveyCard
                v-if="[7.5, 8, 8.5].includes(step)"
                v-model="surveyTheme"
                :title="t('widget.titles.survey-theme-title')"
                :subtitle="t('widget.titles.survey-theme-subtitle')"
                :items="SURVEY_THEME"
                :step="8"
                @starting="() => onVenueHost(7)"
                @exiting="() => onVenueHost(7.5)"
                @next="step = $event"
            />
            <AmountCard
                v-if="[8.5, 9, 9.5].includes(step)"
                v-model="guestAmount"
                :name="bookerName"
                :step="9"
                :max-guest-amount="maxGuestAmount"
                :min-guest-amount="minGuestAmount"
                :guest-amount-step="guestAmountStep"
                :event="eventName"
                @started="() => onVenueHost(8)"
                @next="step = $event"
            />
            <OptionsCard
                v-if="[9.5, 10, 10.5].includes(step)"
                v-model="descriptionIds"
                :items="DESCRIPTIONS"
                :step="10"
                :title="t('widget.descriptions.title')"
                :subtitle="t('widget.descriptions.subtitle')"
                multiple
                with-button
                @next="step = $event"
            />
            <BudgetCard
                v-if="[10.5, 11, 11.5].includes(step)"
                v-model="budgetAmount"
                :step="11"
                :max-budget-amount="maxBudgetAmount"
                :min-budget-amount="minBudgetAmount"
                :budget-step="budgetStep"
                @next="step = $event"
            />
            <AppointmentCard
                v-if="[11.5, 12, 12.5, 14.5].includes(step)"
                v-model="appointment"
                :step="12"
                @next="step = $event"
            />
            <AppointmentCalendarCard
                v-if="[12.5, 13, 13.5].includes(step)"
                v-model="appointmentDate"
                :step="13"
                @next="step = $event"
                @started="() => onVenueHost(12)"
                @exited="() => onVenueHost(12.5)"
            />
            <!-- This is the end of the path so step has to be further from the last in path to avoid overlapping -->
            <LoadingCard
                v-if="[13.5, 14.5, 15, 15.5].includes(step)"
                :step="15"
                :title="t('widget.loading.title')"
                :subtitle="t('widget.loading.subtitle')"
                animate
                @next="step = $event"
            />
            <OptionsCard
                v-if="[15.5, 16, 16.5].includes(step)"
                :items="finalSelectionMap"
                :step="16"
                :title="t('widget.summary.title', { name: bookerName })"
                read-only
                auto-exit
                @next="step = $event"
            />
            <PreTextCard
                v-if="[16.5, 17, 17.5].includes(step)"
                :step="17"
                :event="eventName"
                @next="step = $event"
            />
            <FinalResultCard
                v-if="[17.5, 18].includes(step)"
                :venue="venue"
                :name="bookerName"
                :date="selectedDate"
                :locale="locale"
                :error="error"
                :guests="guestAmount"
                :total-guest="maxGuestAmount"
                :event-type="eventTypeId"
                :event-types="venueEventTypes"
                :message-data="{
                    surveyOrganizer,
                    surveyTheme,
                    surveyAppointment,
                    descriptionIds,
                    budgetAmount,
                    appointmentDate,
                }"
                @back-to-main="handleToMain"
            />
        </template>
    </div>
</template>

<style lang="scss" scoped>
.path-view {
    overflow: hidden;
    block-size: 100%;
    inline-size: 100%;

    &__scroll {
        display: flex;
        block-size: 100%;
        inline-size: 100%;
        min-block-size: 100%;
        -ms-overflow-style: none; /* IE & Edge */
        overflow-y: auto;
        scrollbar-width: none; /* Firefox */

        &::-webkit-scrollbar {
            display: none; /* Chrome, Safari */
        }
    }
}
</style>
